import React from 'react';
import {
  Headset,
  Smartphone,
  Brain,
  Box,
  DollarSign,
  FileText,
} from 'lucide-react';

const PlatformCapabilities = () => {
  const capabilities = [
    {
      icon: Smartphone,
      title: 'AR on Your Phone',
      description:
        'Use your iPhone or Android to see designs in your actual space. No special equipment needed.',
      features: ['iOS ARKit', 'Android ARCore', 'Instant preview'],
    },
    {
      icon: Brain,
      title: 'AI Design Assistant',
      description:
        'Get smart suggestions for furniture, colors, and layouts that match your style and budget.',
      features: ['Style matching', 'Budget optimization', 'Smart recommendations'],
    },
    {
      icon: DollarSign,
      title: 'Transparent Pricing',
      description:
        'See real costs instantly. Compare prices from verified partners. No hidden fees or surprises.',
      features: ['Live pricing', 'Cost breakdowns', 'Price comparison'],
    },
    {
      icon: FileText,
      title: 'Complete Shopping List',
      description:
        'Get detailed lists of everything you need with exact quantities, brands, and specifications.',
      features: ['Auto-generated lists', 'Product specifications', 'Quantity calculation'],
    },
    {
      icon: Box,
      title: 'Direct Ordering',
      description:
        'Order furniture, materials, and installation services directly through the platform.',
      features: ['One-click ordering', 'Trusted partners', 'Delivery tracking'],
    },
    {
      icon: Headset,
      title: 'Virtual Walkthrough',
      description:
        'Walk through your space before it exists. Make changes until it\'s exactly what you want.',
      features: ['Real-time editing', 'Save designs', 'Share with family'],
    },
  ];

  return (
    <section id="platform" className="relative py-32 px-6 md:px-[7.6923%]" style={{
      background: 'linear-gradient(to bottom, #FFFFFF 0%, #FFF5E6 50%, #FFFFFF 100%)'
    }}>
      {/* Grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg, transparent, transparent 1px, transparent 1px, transparent 7.6923%), repeating-linear-gradient(-90deg, #fff, #fff 1px, transparent 1px, transparent 7.6923%)',
          backgroundSize: '100% 100%',
          opacity: 0.03,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-20">
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-semibold mb-6"
            style={{
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
            }}
          >
            Everything You Need in One Platform
          </h2>
          <p
            className="text-lg md:text-xl max-w-3xl mx-auto"
            style={{ color: 'var(--text-secondary)' }}
          >
            From visualization to ordering to installation — we've thought of everything so you don't have to.
          </p>
        </div>

        {/* Capabilities grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {capabilities.map((capability, index) => (
            <div
              key={index}
              className="group glass p-8 rounded-3xl transition-all duration-400 hover:shadow-2xl hover:scale-105 border-2 border-orange-200 hover:border-orange-400"
            >
              {/* Icon */}
              <div className="mb-6">
                <capability.icon size={48} color="#FF8000" strokeWidth={1.5} />
              </div>

              {/* Title */}
              <h3
                className="text-2xl font-semibold mb-4"
                style={{ color: 'var(--text-primary)' }}
              >
                {capability.title}
              </h3>

              {/* Description */}
              <p
                className="text-base mb-6"
                style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}
              >
                {capability.description}
              </p>

              {/* Features list */}
              <ul className="space-y-2">
                {capability.features.map((feature, idx) => (
                  <li
                    key={idx}
                    className="flex items-center gap-2 text-sm"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    <span
                      className="w-1.5 h-1.5 flex-shrink-0"
                      style={{ backgroundColor: 'var(--brand-primary)' }}
                    />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Feature showcase images */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="relative overflow-hidden group">
            <img
              src="https://images.pexels.com/photos/35567640/pexels-photo-35567640.jpeg"
              alt="Technology"
              className="w-full h-[300px] object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
            <div
              className="absolute inset-0 border-2 transition-opacity duration-400 opacity-0 group-hover:opacity-100"
              style={{ borderColor: 'var(--brand-primary)' }}
            />
          </div>
          <div className="relative overflow-hidden group">
            <img
              src="https://images.pexels.com/photos/23232336/pexels-photo-23232336.jpeg"
              alt="Innovation"
              className="w-full h-[300px] object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
            <div
              className="absolute inset-0 border-2 transition-opacity duration-400 opacity-0 group-hover:opacity-100"
              style={{ borderColor: 'var(--brand-primary)' }}
            />
          </div>
          <div className="relative overflow-hidden group">
            <img
              src="https://images.pexels.com/photos/34642280/pexels-photo-34642280.jpeg"
              alt="Architecture"
              className="w-full h-[300px] object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
            <div
              className="absolute inset-0 border-2 transition-opacity duration-400 opacity-0 group-hover:opacity-100"
              style={{ borderColor: 'var(--brand-primary)' }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlatformCapabilities;